import { useState } from "react";

type ActionKey = "withdraw" | "cancel" | "approve" | "reject" | "delete" | string;

interface ActionState {
  open: boolean;
  data: unknown;
}

/**
 * Manages open/close state for one or more request action dialogs.
 *
 * Consolidates per-page `useState` scattered across detail pages into
 * a single hook. Useful for My Space pages with a withdraw/cancel dialog.
 * For complex company pages with workflow branching, inline state is fine.
 *
 * @example
 * const actions = useRequestActions(["withdraw"]);
 * // open with optional payload:
 * actions.open("withdraw", { requestId: item.id });
 * // in JSX:
 * <WithdrawDialog
 *   open={actions.isOpen("withdraw")}
 *   onClose={() => actions.close("withdraw")}
 *   data={actions.getData("withdraw")}
 * />
 */
export function useRequestActions<T = unknown>(keys: ActionKey[]) {
  const initial = Object.fromEntries(
    keys.map((k) => [k, { open: false, data: null }]),
  ) as Record<ActionKey, ActionState>;

  const [state, setState] = useState<Record<ActionKey, ActionState>>(initial);

  const open = (key: ActionKey, data?: T) =>
    setState((prev) => ({ ...prev, [key]: { open: true, data: data ?? null } }));

  const close = (key: ActionKey) =>
    setState((prev) => ({ ...prev, [key]: { open: false, data: null } }));

  const isOpen = (key: ActionKey) => state[key]?.open ?? false;

  const getData = (key: ActionKey) => state[key]?.data as T | null;

  return { open, close, isOpen, getData };
}
